import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api';

interface StoreConsoleError {
  store: string;
  storeLabel: string;
  message: string;
}

interface StoreChannel {
  platform: 'ios' | 'android';
  channel: 'production' | 'beta' | 'dev';
  version: string | null;
  build: string | null;
  status: string | null;
  releaseNotes: string | null;
  uploadedAt: string | null;
  createdAt: string | null;
  phasedAt: string | null;
  source: string;
  group: string | null;
  active: boolean;
}

const platformLabel = (platform: StoreChannel['platform']) =>
  platform === 'ios' ? 'iOS' : 'Android';

const deploymentBadgeClass = (deployment: string, active: boolean) => {
  if (!active) return 'bg-secondary';
  switch (deployment) {
    case 'production':
      return 'bg-success';
    case 'beta':
      return 'bg-primary';
    case 'dev':
    default:
      return 'bg-dark';
  }
};

const formatDate = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const channelDates = (row: StoreChannel) => {
  const parts: string[] = [];
  const created = formatDate(row.createdAt);
  const uploaded = formatDate(row.uploadedAt);
  const phased = formatDate(row.phasedAt);
  if (created) parts.push(`Created ${created}`);
  if (uploaded) parts.push(`Uploaded ${uploaded}`);
  if (phased) parts.push(`Phased ${phased}`);
  return parts.length ? parts.join('\n') : '—';
};

const generatedByLabel = (generatedBy: string | null) => {
  if (generatedBy === 'cron') return 'scheduled job';
  if (generatedBy === 'manual') return 'manual';
  return null;
};

interface MobileAppManagementProps {
  publicView?: boolean;
}

const MobileAppManagement: React.FC<MobileAppManagementProps> = ({ publicView = false }) => {
  const [channels, setChannels] = useState<StoreChannel[]>([]);
  const [consoleErrors, setConsoleErrors] = useState<StoreConsoleError[]>([]);
  const [consoleLoading, setConsoleLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [consoleError, setConsoleError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [generatedBy, setGeneratedBy] = useState<string | null>(null);

  const applyConsolePayload = (data: any) => {
    setChannels(data.channels || []);
    setConsoleErrors(data.errors || []);
    setGeneratedAt(data.generatedAt || data.fetchedAt || null);
    setGeneratedBy(data.generatedBy || null);
  };

  const fetchStoreConsole = async () => {
    setConsoleLoading(true);
    setConsoleError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/mobile-app/store-console`,
        publicView ? undefined : { withCredentials: true }
      );
      applyConsolePayload(response.data);
    } catch (err: any) {
      applyConsolePayload(err.response?.data || {});
      setConsoleError(
        err.response?.data?.message || 'Failed to fetch store console data'
      );
    } finally {
      setConsoleLoading(false);
    }
  };

  const generateManifest = async () => {
    setGenerating(true);
    setConsoleError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/mobile-app/manifest/generate`,
        {},
        { withCredentials: true }
      );
      applyConsolePayload(response.data);
      setMessage('Manifest generated from latest App Store / Play Console data.');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      applyConsolePayload(err.response?.data || {});
      setConsoleError(
        err.response?.data?.message || 'Failed to generate mobile app manifest'
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchStoreConsole();
  }, []);

  const lastGeneratedLabel = formatDate(generatedAt);
  const sourceLabel = generatedByLabel(generatedBy);

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-3">
        <div className="col">
          <h3>Mobile App</h3>
          <p className="text-muted mb-0">
            Production, beta, and dev from the App Store and Play Console.
            {!publicView && (
              <>
                {' '}Active rows feed{' '}
                <code>{API_BASE_URL}/mobile-app/manifest</code>. Beta/dev that match
                production are inactive so apps on that version stay on production.
              </>
            )}
          </p>
        </div>
      </div>

      {!publicView && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Manifest generation</h5>
            <p className="text-muted mb-3">
              The server automatically regenerates the manifest every 15 minutes.
              Use the button below after you ship if you need it sooner.
            </p>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={generateManifest}
                disabled={generating || consoleLoading}
              >
                <FontAwesomeIcon icon={faSync} className="me-2" />
                {generating ? 'Generating...' : 'Generate Manifest'}
              </button>
              <div className="text-muted">
                <strong>Last manifest generation:</strong>{' '}
                {lastGeneratedLabel || '—'}
                {sourceLabel ? ` (${sourceLabel})` : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {consoleError && <div className="alert alert-danger">{consoleError}</div>}
      {message && <div className="alert alert-success">{message}</div>}
      {consoleErrors.map((err) => (
        <div key={err.store} className="alert alert-warning">
          {err.storeLabel}: {err.message}
        </div>
      ))}

      {consoleLoading && channels.length === 0 ? (
        <p>Loading store releases...</p>
      ) : (
        <div className="table-responsive mb-5">
          <table className="table table-striped table-sm align-middle">
            <thead>
              <tr>
                <th>Deployment</th>
                <th>Platform</th>
                <th>Version</th>
                <th>Status</th>
                <th>Dates</th>
                <th>Release notes</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {channels.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted">
                    No store release data available.
                  </td>
                </tr>
              ) : (
                channels.map((row) => (
                  <tr
                    key={`${row.platform}-${row.channel}`}
                    className={row.active ? undefined : 'table-secondary'}
                    style={
                      row.active
                        ? undefined
                        : { opacity: 0.55, color: '#6c757d' }
                    }
                    title={
                      row.active
                        ? undefined
                        : 'Same version as production — inactive for manifest / testing tools'
                    }
                  >
                    <td>
                      <span
                        className={`badge ${deploymentBadgeClass(
                          row.channel,
                          row.active
                        )}`}
                      >
                        {row.channel}
                      </span>
                      {!row.active && (
                        <span className="ms-2 small text-muted">inactive</span>
                      )}
                    </td>
                    <td>{platformLabel(row.platform)}</td>
                    <td>
                      {row.version ?? '—'}
                      {row.platform === 'ios' && row.build ? (
                        <span className="text-muted"> ({row.build})</span>
                      ) : null}
                    </td>
                    <td>{row.status ?? '—'}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>
                      {channelDates(row)}
                    </td>
                    <td
                      style={{
                        maxWidth: 420,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {row.releaseNotes || '—'}
                    </td>
                    <td>
                      {row.source}
                      {row.group ? ` · ${row.group}` : ''}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MobileAppManagement;
